import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { useConfig } from "./config";
import "./Admin.css";
import FileUploader from "./components/FileUploader";
import "./components/FileUploader.css";

// SyncStatus Component

function Admin() {
  const { config } = useConfig();
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedBot, setSelectedBot] = useState(null);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const queryClient = useQueryClient();
  const [expandedUser, setExpandedUser] = useState(null);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserIsAdmin, setNewUserIsAdmin] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkboxStates, setCheckboxStates] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [isReuploading, setIsReuploading] = useState(false);
  const [manualAssistantLoading, setManualAssistantLoading] = useState(false);
  const [fileMode, setFileMode] = useState(null); // "manual" or "auto"
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20); // Default to 20 items per page

  const navigate = useNavigate();
  const clientId = `admin_${Date.now()}`;
  const { getVersionedAssetUrl } = useConfig();
  const userId = localStorage.getItem("user_id");
  if (!userId) {
    
    navigate("/login");
  }
  function getCookie(cname) {
    let name = cname + "=";
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(';');
    for(let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return "";
  }
  const user_type = getCookie("user_type");
  if (user_type != 'Admin')
  {
    navigate("/hr");
  }
  const SyncStatus = () => {
    const [syncData, setSyncData] = useState({
      last_sync: null,
      status: 'unknown',
      message: 'Loading...',
      stats: {}
    });
    const [loading, setLoading] = useState(true);
    
    const fetchSyncStatus = async () => {
      try {
        const response = await fetch(`${config.API_BASE_URL}/api/admin/vstore/sync-status`);
        const data = await response.json();
        setSyncData(data);
      } catch (error) {
        console.error('Error fetching sync status:', error);
        setSyncData({
          last_sync: null,
          status: 'error',
          message: 'Failed to load sync status',
          stats: {}
        });
      } finally {
        setLoading(false);
      }
    };
  
    useEffect(() => {
      fetchSyncStatus();
      // Refresh every 30 seconds
      const interval = setInterval(fetchSyncStatus, 30000);
      return () => clearInterval(interval);
    }, []);
    
  
    const formatLastSync = (lastSync) => {
      if (!lastSync) return 'Never';
      
      try {
        const date = new Date(lastSync);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);
  
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        return `${diffDays}d ago`;
      } catch (error) {
        return 'Invalid date';
      }
    };
  
    const getStatusColor = (status) => {
      switch (status) {
        case 'success': return '#28a745';
        case 'error': return '#dc3545';
        case 'running': return '#ffc107';
        default: return '#6c757d';
      }
    };
  
    const getStatusIcon = (status) => {
      switch (status) {
        case 'success': return '✅';
        case 'error': return '❌';
        case 'running': return '🔄';
        default: return '❓';
      }
    };
  
    if (loading) {
      return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          fontSize: '0.9em',
          color: '#6c757d'
        }}>
          <span>🔄</span>
          <span>Loading sync status...</span>
        </div>
      );
    }
  
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        fontSize: '0.9em',
        padding: '8px 12px',
        backgroundColor: '#f8f9fa',
        borderRadius: '6px',
        border: '1px solid #dee2e6'
      }}>
        <span style={{ fontSize: '1.2em' }}>
          {getStatusIcon(syncData.status)}
        </span>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <div style={{ 
            color: getStatusColor(syncData.status),
            fontWeight: '500'
          }}>
            Last sync: {formatLastSync(syncData.last_sync)}
          </div>
          
          <div style={{ 
            color: syncData.status === 'error' ? '#dc3545' : '#6c757d',
            fontSize: '0.8em',
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {syncData.message}
          </div>
          
          {syncData.stats && Object.keys(syncData.stats).length > 0 && (
            <div style={{ 
              fontSize: '0.75em',
              color: '#6c757d',
              display: 'flex',
              gap: '8px'
            }}>
              {syncData.stats.new_files > 0 && (
                <span style={{ color: '#28a745' }}>+{syncData.stats.new_files}</span>
              )}
              {syncData.stats.updated_files > 0 && (
                <span style={{ color: '#ffc107' }}>~{syncData.stats.updated_files}</span>
              )}
              {syncData.stats.deleted_files > 0 && (
                <span style={{ color: '#dc3545' }}>-{syncData.stats.deleted_files}</span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };
  
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = getVersionedAssetUrl('Admin.css');
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, [getVersionedAssetUrl]);
  useEffect(() => {
    const fetchUsers = async () => {
    const response = await fetch(`${config.API_BASE_URL}/api/admin/users/${encodeURIComponent(localStorage.getItem("email"))}`);

    // For testing, don't redirect to login
    if (!response.ok) {
      const errorData = await response.json();
      //console.error("Error fetching users:", errorData);
      // Return empty array instead of throwing error
      return [];
    }

    const data = await response.json();

    if (data.length > 0 && data[0].user_type != 'Admin')
    {
      
      navigate("/hr");
    }
    return ;
  };
  fetchUsers();
  }, []);

  

  const fetchmode = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/admin/vstore/files?client_id=${clientId}`);
      if (!response.ok) {
        const errorData = await response.json();
        //console.error("Error fetching mode:", errorData);
        return;
      }
      const data = await response.json();
      if (data.status === "processing") {
        // Show processing status
        toast.success("File sync started in background");
      }
    } catch (error) {
      //console.error("Error in fetchmode:", error);
      toast.error("Error starting file sync");
    }
  };

  const {
    data: usersData,
    isLoading: usersLoading,
    error: usersError,
  } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 3000));
        const response = await fetch(`${config.API_BASE_URL}/api/admin/users`);
        
        // For testing, don't redirect to login
        if (!response.ok) {
          const errorData = await response.json();
          //console.error("Error fetching users:", errorData);
          // Return empty array instead of throwing error
          return [];
        }

        return response.json();
      } catch (error) {
        //console.error("Error fetching users:", error);
        // Return empty array instead of throwing error
        return [];
      }
    },
  });

  // Fetch assistant files when a bot is selected
  const {
    data: assistantInfo,
    isLoading: assistantLoading,
    error: assistantError,
    refetch: refetchAssistantInfo,
  } = useQuery({
    queryKey: ["assistant", selectedBot?.assistant_id],
    queryFn: async () => {
      if (!selectedBot?.assistant_id) return null;
      //console.log("Fetching assistant info for ID:", selectedBot.assistant_id);
      const response = await fetch(`${config.API_BASE_URL}/api/assistants/info/${selectedBot.assistant_id}`);
      if (!response.ok) {
        const errorData = await response.json();
        //console.error("Error fetching assistant info:", errorData);
        throw new Error(errorData.detail || "Failed to fetch assistant info");
      }
      const data = await response.json();
      // if (data.status === "processing") {
      //   toast.success("File sync started in background");
      // }
      return data;
    },
    enabled: !!selectedBot?.assistant_id,
    retry: 1,
  });

  // Fetch assistant info with metadata files
  const {
    data: assistantInfoData,
    isLoading: assistantInfoLoading,
    error: assistantInfoError,
    refetch: refetchAssistantInfoData,
  } = useQuery({
    queryKey: ["assistant-info", selectedBot?.assistant_id],
    queryFn: async () => {
      if (!selectedBot?.assistant_id) return null;
      console.log("🔍 Calling assistant info API:", `${config.API_BASE_URL}/api/assistants/info/${selectedBot.assistant_id}`);
      
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
        
        const response = await fetch(
          `${config.API_BASE_URL}/api/assistants/info/${selectedBot.assistant_id}`,
          { signal: controller.signal }
        );
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const errorData = await response.json();
          console.error("❌ Assistant info API error:", errorData);
          throw new Error(errorData.detail || "Failed to fetch assistant info");
        }
        
        const data = await response.json();
        console.log("✅ Assistant info API response:", data);
        return data;
      } catch (error) {
        console.error("❌ Assistant info API timeout or error:", error);
        return null;
      }
    },
    enabled: !!selectedBot?.assistant_id,
  });

  const uploadFileApi = async (file, force = false) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(`/api/assistants/files/${selectedBot.assistant_id}?force=${force}`, {
      method: "POST",
      body: formData,
    });
    return response;
  };

  const handleReuploadConfirmed = (file, filename) => {
    setIsReuploading(true);
    // setManualAssistantLoading(true);
    // Create a new pending ID for this re-upload
    const pendingId = `pending-${Date.now()}`;
  
    // Add the file to the pending files list with "Uploading..." status
    const pendingFile = {
      file_id: pendingId,
      filename: filename,
      purpose: "Uploading...",
      bytes: file.size,
      created_at: Math.floor(Date.now() / 1000),
      status: "uploading",
    };
  
    setPendingFiles((prev) => [...prev, pendingFile]);
  
    // Create form data
    const formData = new FormData();
    formData.append("file", file);
    formData.append("purpose", "assistants");
  
    // Start the upload with force=true
    uploadFileMutation.mutate({
      formData,
      pendingId,
      filename,
      force: true, // Pass this to your mutation
    },
    {
      onSettled: () => {
        setIsReuploading(false); // Hide loader when upload is done (success or error)
        setShowModal(false);
        setPendingFile(null);
        // setManualAssistantLoading(false);

      },
    });
  
    setShowModal(false);
    setPendingFile(null);
  };

  const handleCancelReupload = () => {
    setShowModal(false);
    setPendingFile(null);
  };

  // Upload file mutation
  const uploadFileMutation = useMutation({
    mutationFn: async ({ formData, pendingId, filename,file,force = false }) => {
      //console.log("Uploading file to assistant ID:", selectedBot.assistant_id);

      try {
        const response = await fetch(
          `${config.API_BASE_URL}/api/assistants/files/${selectedBot.assistant_id}?force=${force}`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          //console.error("Error uploading file:", errorData);
          if (response.status === 409) {
            setPendingFile(file); // Save the file for re-upload
            setShowModal(true);
          }
          
          throw new Error(errorData.detail || "Failed to upload file");
        }

        const data = await response.json();
        //console.log("File upload successful:", data);
        return { data, pendingId, filename };
      } catch (error) {
        //console.error("Error in file upload mutation:", error);
        throw error;
      }
    },
    onSuccess: async ({ data, pendingId, filename }) => {
      toast.success(`File "${filename}" uploaded successfully`);
      setFileToUpload(null);

      // Remove the pending file
      setPendingFiles((prev) =>
        prev.filter((file) => file.file_id !== pendingId)
      );

      // Refresh the assistant info to get the updated file list
      
      setManualAssistantLoading(true);
      await refetchAssistantInfo();
      await refetchAssistantInfoData();
      setManualAssistantLoading(false);
    },
    onError: (error, { pendingId, filename }) => {
      if (error.message !== "File already exist") {
        toast.error(`Upload of "${filename}" failed: ${error.message}`);
      }


      // Remove the pending file
      setPendingFiles((prev) =>
        prev.filter((file) => file.file_id !== pendingId)
      );
    },
  });

  // Delete file mutation
  const deleteFileMutation = useMutation({
    mutationFn: async ({ fileId, filename }) => {
      const response = await fetch(
        `${config.API_BASE_URL}/api/assistants/files/${selectedBot.assistant_id}/${fileId}`,
        {
          method: "DELETE",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to delete file");
      }
      return { fileId, filename };
    },
    onSuccess: async ({ fileId, filename }) => {
      toast.success(`File "${filename}" deleted successfully`);

      // Refresh the assistant info to get the updated file list
      await refetchAssistantInfo();
      await refetchAssistantInfoData();
    },
    onError: (error, { fileId, filename }) => {
      toast.error(`Delete of "${filename}" failed: ${error.meDssage}`);

      // Revert the file status back to "Ready"
      const updatedFiles = assistantInfo.file_details.map((file) =>
        file.file_id === fileId
          ? { ...file, purpose: "Ready", status: "ready" }
          : file
      );

      // Update the cache with the reverted status
      queryClient.setQueryData(
        ["assistant", selectedBot?.assistant_id],
        (old) => (old ? { ...old, file_details: updatedFiles } : old)
      );
    },
  });

  // Add this mutation for deactivating users
  const deactivateUserMutation = useMutation({
    mutationFn: async (email) => {
      const response = await fetch(
        `${config.API_BASE_URL}/api/admin/users/${email}`,
        {
          method: "DELETE",
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to deactivate user");
      }
      return email;
    },
    onSuccess: (email) => {
      toast.success(`User ${email} deactivated successfully`);
      // If the deactivated user was expanded, collapse it
      if (expandedUser === email) {
        setExpandedUser(null);
      }
      // Refresh the users list
      queryClient.invalidateQueries(["users"]);
    },
    onError: (error) => {
      toast.error(`Deactivation failed: ${error.message}`);
    },
  });

  // Add this mutation for creating users
  const createUserMutation = useMutation({
    mutationFn: async (userData) => {
      const response = await fetch(`${config.API_BASE_URL}/api/admin/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to create user");
      }

      return await response.json();
    },
    onSuccess: () => {
      toast.success(`User ${newUserEmail} created successfully`);
      setIsAddUserModalOpen(false);
      setNewUserEmail("");
      setNewUserIsAdmin(false);
      // Refresh the users list
      queryClient.invalidateQueries(["users"]);
    },
    onError: (error) => {
      toast.error(`Create user failed: ${error.message}`);
    },
    onSettled: () => {
      setIsCreatingUser(false);
    },
  });

  // Handle file upload
  const handleFileUpload = (file) => {
    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }

    // Create a unique ID for this pending file
    const pendingId = `pending-${Date.now()}`;

    // Add the file to the pending files list with "Uploading..." status
    const pendingFile = {
      file_id: pendingId,
      filename: file.name,
      purpose: "Uploading...",
      bytes: file.size,
      created_at: Math.floor(Date.now() / 1000),
      status: "uploading",
    };

    setPendingFiles((prev) => [...prev, pendingFile]);

    // Create form data
    const formData = new FormData();
    formData.append("file", file);
    formData.append("purpose", "assistants");

    // Start the upload
    uploadFileMutation.mutate({
      formData,
      pendingId,
      filename: file.name,
      file,
      force: false,
    });
  };

  // Handle file deletion
  const handleFileDelete = (fileId, filename) => {
    // Update the file status to "Deleting..."
    const updatedFiles = assistantInfo.file_details.map((file) =>
      file.file_id === fileId
        ? { ...file, purpose: "Deleting...", status: "deleting" }
        : file
    );

    // Update the cache with the new status
    queryClient.setQueryData(["assistant", selectedBot?.assistant_id], (old) =>
      old ? { ...old, file_details: updatedFiles } : old
    );

    // Start the deletion
    deleteFileMutation.mutate({
      fileId,
      filename,
    });
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  // Update the formatDate function to handle OpenAI's date format
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      // Check if it's a Unix timestamp (OpenAI sometimes returns these)
      if (typeof dateString === "number") {
        const date = new Date(dateString * 1000); // Convert seconds to milliseconds
        return date.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }

      // Try to parse the date string
      const date = new Date(dateString);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        //console.error("Invalid date format:", dateString);
        return "Invalid date";
      }

      // Check if the date string includes time information
      const hasTimeInfo =
        dateString.includes("T") ||
        dateString.includes(":") ||
        (dateString.includes(" ") && dateString.split(" ")[1].includes(":"));

      // For date-only strings (like YYYY-MM-DD), use the date directly without timezone adjustment
      if (!hasTimeInfo) {
        // Split the date string and create a new date with explicit parts
        const [year, month, day] = dateString
          .split("-")
          .map((num) => parseInt(num, 10));

        return new Date(year, month - 1, day).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } else {
        // For dates with time info, use the standard formatting
        return date.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    } catch (error) {
      //console.error("Error formatting date:", error, "for input:", dateString);
      return "Invalid date";
    }
  };

  // Get all files (including pending ones)
  const getAllFiles = () => {
    const assistantFiles = assistantInfo?.file_details || [];

    // Map assistant files to have "Ready" as purpose if not already set
    const mappedAssistantFiles = assistantFiles.map((file) => ({
      ...file,
      purpose: file.purpose === "assistants" ? "Ready" : file.purpose,
    }));

    // Combine with pending files
    return [...mappedAssistantFiles, ...pendingFiles];
  };

  // Pagination helper functions
  const getPaginatedFiles = () => {
    console.log("🔍 getPaginatedFiles called:", { assistantInfoData, currentPage, itemsPerPage });
    
    // Use assistant info data if available, otherwise fall back to client-side pagination
    if (assistantInfoData && assistantInfoData.length > 0) {
      console.log("✅ Using assistant info data:", assistantInfoData.length, "files");
      // Apply client-side pagination to the assistantInfoData array
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedFiles = assistantInfoData.slice(startIndex, endIndex);
      console.log("📄 Paginated files:", paginatedFiles.length, "files from", assistantInfoData.length, "total");
      return paginatedFiles;
    }
    
    // Fallback to client-side pagination
    const allFiles = getAllFiles();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const fallbackFiles = allFiles.slice(startIndex, endIndex);
    console.log("🔄 Using fallback data:", fallbackFiles.length, "files from", allFiles.length, "total");
    return fallbackFiles;
  };

  const getTotalPages = () => {
    // Use assistant info data if available, otherwise fall back to client-side pagination
    if (assistantInfoData && assistantInfoData.length > 0) {
      return Math.ceil(assistantInfoData.length / itemsPerPage);
    }
    
    // Fallback to client-side pagination
    const allFiles = getAllFiles();
    return Math.ceil(allFiles.length / itemsPerPage);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Get skeleton rows for loading state
  const getSkeletonRows = () => {
    return Array(3)
      .fill()
      .map((_, i) => ({
        file_id: `skeleton-${i}`,
        filename: "",
        purpose: "Loading...",
        bytes: 0,
        created_at: 0,
      }));
  };

  // Add an effect to listen for the upload-file event
  useEffect(() => {
    const handleUploadEvent = (e) => {
      if (e.detail && e.detail.file) {
        handleFileUpload(e.detail.file);
      }
    };

    document.addEventListener("upload-file", handleUploadEvent);

    return () => {
      document.removeEventListener("upload-file", handleUploadEvent);
    };
  }, [selectedBot]);

  // Handle user card click
  const handleUserCardClick = (email) => {
    // Toggle expanded state
    setExpandedUser(expandedUser === email ? null : email);
    // Don't set selectedUser anymore since we're expanding in place
    setSelectedUser(null);
  };

  // Handle user deactivation
  const handleUserDeactivate = (email, e) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to deactivate user ${email}?`)) {
      deactivateUserMutation.mutate(email);
    }
  };

  // Add this function to handle user creation
  const handleCreateUser = (e) => {
    e.preventDefault();

    if (!newUserEmail) {
      toast.error("Please enter an email address");
      return;
    }

    setIsCreatingUser(true);

    createUserMutation.mutate({
      email: newUserEmail,
      is_active: true,
      role: newUserIsAdmin ? "ADMIN" : "USER",
    });
  };

  // Add this function to close the modal when clicking outside
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsAddUserModalOpen(false);
      }
    };

    if (isAddUserModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isAddUserModalOpen]);

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, [config]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.API_BASE_URL}/api/admin/users`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }

      const data = await response.json();
      setUsers(data);
    } catch (error) {
      //console.error("Error fetching users:", error);
      setError(error.message);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const toggleUserTypeMutation = useMutation({
    mutationFn: async ({ email, isChecked }) => {
      const requestBody = {
        user_type: isChecked ? "Admin" : "Normal"
      };
      const response = await fetch(
        `${config.API_BASE_URL}/api/admin/users/${encodeURIComponent(email)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
          credentials: "include",
        }
      );
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update user type");
      }
  
      return { email, isChecked };
    },
    onSuccess: ({ email, isChecked }) => {
      toast.success(`User type updated successfully to ${isChecked ? 'Admin' : 'Normal'}`);
      queryClient.setQueryData(['users'], (oldData) => {
        if (!oldData) return oldData;
        
        return oldData.map(user => {
          if (user.email === email) {
            return {
              ...user,
              user_type: isChecked ? 'Admin' : 'Normal'
            };
          }
          return user;
        });
      });
      // Update local checkbox state
      setCheckboxStates(prev => ({
        ...prev,
        [email]: isChecked
      }));
      queryClient.invalidateQueries(['users']);
    },
    onError: (error) => {
      toast.error(`Failed to update user type: ${error.message}`);
    },
  });

  const handleCheckboxChange = (email, currentChecked) => {
    const newCheckedState = !currentChecked;
    toggleUserTypeMutation.mutate({
      email: email,
      isChecked: newCheckedState
    });
  };


  const toggleUserStatus = async (email, isActive) => {
    try {
      // If user is active, deactivate them. If inactive, activate them.
      const action = isActive ? "deactivate" : "activate";

      // Use PUT method instead of DELETE to toggle user status
      const response = await fetch(
        `${config.API_BASE_URL}/api/admin/users/${encodeURIComponent(email)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            is_active: !isActive, // Toggle the active status
          }),
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to ${action} user`);
      }

      // Update the local state to reflect the change
      setUsers(
        users.map((user) =>
          user.email === email ? { ...user, is_active: !isActive } : user
        )
      );

      toast.success(`User ${action}d successfully`);
    } catch (error) {
      // console.error(
      //   `Error ${isActive ? "deactivating" : "activating"} user:`,
      //   error
      // );
      toast.error(error.message);
    }
  };

  const isAssistantReallyLoading = assistantLoading || manualAssistantLoading;

  return (
    
    <div className="admin-container">
      {loading && <div class="loader"></div>}
      <div className="admin-sidebar">
        <div className="sidebar-header">
          <h2>Users</h2>
          <div style={{ 
            fontSize: '0.85em', 
            color: '#666', 
            marginTop: '12px',
            padding: '8px 12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid #e9ecef',
            lineHeight: '1.4'
          }}>
            <div style={{ marginBottom: '4px' }}>
              ✅ All users can access the Chatbot after login
            </div>
            <div style={{ color: '#888', fontSize: '0.9em' }}>
              🔧 Admin rights can be assigned after their first login
            </div>
          </div>
        </div>
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Last Login</th>
                <th>User Type</th>
              </tr>
            </thead>
            <tbody>
              {usersData?.map((user) => (
                <tr key={user.email}>
                  <td>{user.email}</td>
			<td>{new Date(user.last_login).toLocaleDateString()}</td>                  
                  <td>
            <div className="user-type-toggle">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={user.user_type === "Admin"}
                  onChange={() => handleCheckboxChange(
                    user.email,
                    user.user_type === "Admin"
                  )}
                />
                <span className="slider round"></span>
              </label>
              <span className="user-type-label">
                {user.user_type === "Admin" ? "Admin" : "Normal"}
              </span>
            </div>
          </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          
        
        {/* {loading ? (
          <div className="loading-message">Loading users...</div>
        ) : error ? (
          <div className="error-message">Error loading users</div>
        ) : (
          <div className="user-list">
            {users && users.length > 0 ? (
              users.map((user) => (
                <div
                  key={user.email}
                  className={`user-card ${
                    expandedUser === user.email ? "expanded" : ""
                  }`}
                  onClick={() => handleUserCardClick(user.email)}
                >
                  <div className="user-card-header">
                    <div className="user-email">{user.email}</div>
                  </div>

                  <div className="user-card-subheader">
                    <div className="user-status-indicator">
                      <span
                        className={`status-dot ${
                          user.is_active ? "active" : "inactive"
                        }`}
                      ></span>
                      <span className="status-text">
                        {user.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>

                    <div className={`user-role ${user.role.toLowerCase()}`}>
                      {user.role}
                    </div>
                  </div>

                  {expandedUser === user.email && (
                    <div className="user-card-details">
                      <div className="detail-row">
                        <span className="detail-label">Status:</span>
                        <span
                          className={`detail-value ${
                            user.is_active ? "active" : "inactive"
                          }`}
                        >
                          {user.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">Role:</span>
                        <span
                          className={`detail-value role-${user.role.toLowerCase()}`}
                        >
                          {user.role}
                        </span>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">Last Login:</span>
                        <span className="detail-value">
                          {user.last_login
                            ? formatDate(user.last_login)
                            : "Never logged in"}
                        </span>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">Created:</span>
                        <span className="detail-value">
                          {formatDate(user.creation_date)}
                        </span>
                      </div>

                      <div className="user-actions">
                        <button
                          className={
                            user.is_active
                              ? "deactivate-button"
                              : "reactivate-button"
                          }
                          onClick={(e) =>
                            toggleUserStatus(user.email, user.is_active)
                          }
                          disabled={deactivateUserMutation.isLoading}
                        >
                          {user.is_active ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-users">No users found</div>
            )}
          </div>
        )} */}

        {/* Add User Modal */}
        {isAddUserModalOpen && (
          <div className="modal-overlay">
            <div className="modal-container" ref={modalRef}>
              <div className="modal-header">
                <h3>Add New User</h3>
                <button
                  className="modal-close-button"
                  onClick={() => setIsAddUserModalOpen(false)}
                >
                  &times;
                </button>
              </div>
              <form onSubmit={handleCreateUser} className="modal-form">
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    placeholder="user@example.com"
                    required
                  />
                </div>
                <div className="form-group checkbox-group">
                  <input
                    type="checkbox"
                    id="isAdmin"
                    checked={newUserIsAdmin}
                    onChange={(e) => setNewUserIsAdmin(e.target.checked)}
                  />
                  <label htmlFor="isAdmin">Admin User</label>
                </div>
                <div className="form-actions">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => setIsAddUserModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={isCreatingUser}
                  >
                    {isCreatingUser ? "Creating..." : "Create User"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      <div className="admin-content">
        <div className="admin-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2>Assistants</h2>
            <SyncStatus />
          </div>
          
          <div className="bot-list">
            {config.BOTS?.map((bot) => (
              <div
                key={bot.slug}
                className={`bot-item ${
                  selectedBot?.slug === bot.slug ? "selected" : ""
                }`}
                onClick={() => setSelectedBot(bot)}
              >
                <h3>{bot.title}</h3>
                <p>{bot.description}</p>
              </div>
            ))}
          </div>
        </div>

        {selectedBot && (
          <div className="admin-section">
            <h2>Files for {selectedBot.title}</h2>

            {fileMode === "manual" && (
              <div className="file-upload-form">
                <h3>Upload a new file</h3>
                <FileUploader
                  onFileSelected={(file) => {
                    setFileToUpload(file);
                  }}
                  isUploading={uploadFileMutation.isLoading}
                />
              </div>
            )}
            <div
              className={`file-table-container ${
                isAssistantReallyLoading || assistantInfoLoading ? "loading" : ""
              }`}
            >
              <table className="file-table">
                <thead>
                  <tr>
                    <th>Metadata Files</th>
                    <th>Status</th>
                    <th>Size</th>
                    <th>Modified</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isAssistantReallyLoading || assistantInfoLoading ? (
                    // Show skeleton rows while loading
                    getSkeletonRows().map((skeleton) => (
                      <tr key={skeleton.file_id} className="skeleton-row">
                        <td>
                          <div className="skeleton-text"></div>
                        </td>
                        <td>
                          <div className="skeleton-text"></div>
                        </td>
                        <td>
                          <div className="skeleton-text"></div>
                        </td>
                        <td>
                          <div className="skeleton-text"></div>
                        </td>
                        <td>
                          <div className="skeleton-button"></div>
                        </td>
                      </tr>
                    ))
                  ) : getPaginatedFiles().length > 0 ? (
                    // Show paginated files
                    getPaginatedFiles().map((file) => (
                      <tr
                        key={file.id}
                        className=""
                      >
                        <td>
                          <div>
                            <strong>{file.name}</strong>
                            <div style={{ fontSize: '0.8em', color: '#666', marginTop: '2px' }}>
                              📄 {file.webUrl.split('/sites/')[1]?.split('/')[0] || 'Unknown Site'}/{file.drive}/{file.path.substring(0, file.path.lastIndexOf('/'))}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="file-status processed">
                            Ready
                          </span>
                        </td>
                        <td>{formatFileSize(file.size)}</td>
                        <td>
                          {file.modified
                            ? formatDate(new Date(file.modified).getTime() / 1000)
                            : "Unknown date"}
                        </td>
                        <td>
                        <button
                          className="download-button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Download file using local_path
                            console.log("Downloading file:", file.name, "from:", file.local_path);
                            window.open(`${config.API_BASE_URL}/api/assistants/download-local/${encodeURIComponent(file.local_path)}`, '_blank');
                          }}
                          style={{ backgroundColor: '#28a745', color: 'white', borderRadius: '3px', padding: '5px 10px', border: 'none', cursor: 'pointer' }}
                        >
                          Download
                        </button>

                          {file.purpose === "Uploading..." && (
                            <div className="loading-spinner small"></div>
                          )}
                          {file.purpose === "Deleting..." && (
                            <div className="loading-spinner small"></div>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="no-files">
                        No files attached to this assistant.
                      </td>
                    </tr>
                  )}
                  
                </tbody>
              </table>
              
              {/* Pagination Controls */}
              {(() => {
                const shouldShowPagination = getAllFiles().length > 0 || (assistantInfoData && assistantInfoData.length > 0);
                console.log("🔍 Pagination visibility check:", {
                  getAllFilesLength: getAllFiles().length,
                  assistantInfoLength: assistantInfoData?.length,
                  shouldShow: shouldShowPagination
                });
                return shouldShowPagination;
              })() && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    <span>
                      Showing {Math.min((currentPage - 1) * itemsPerPage + 1, assistantInfoData?.length || getAllFiles().length)} to{" "}
                      {Math.min(currentPage * itemsPerPage, assistantInfoData?.length || getAllFiles().length)} of{" "}
                      {assistantInfoData?.length || getAllFiles().length} files
                    </span>
                    <div className="items-per-page">
                      <label>Items per page:</label>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    >
                      First
                    </button>
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    
                    {/* Page numbers */}
                    <div className="page-numbers">
                      {Array.from({ length: getTotalPages() }, (_, i) => i + 1)
                        .filter(page => {
                          const totalPages = getTotalPages();
                          if (totalPages <= 7) return true;
                          if (page === 1 || page === totalPages) return true;
                          if (page >= currentPage - 1 && page <= currentPage + 1) return true;
                          return false;
                        })
                        .map((page, index, array) => {
                          const totalPages = getTotalPages();
                          const showEllipsis = index > 0 && page - array[index - 1] > 1;
                          
                          return (
                            <React.Fragment key={page}>
                              {showEllipsis && <span className="pagination-ellipsis">...</span>}
                              <button
                                className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                                onClick={() => handlePageChange(page)}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          );
                        })}
                    </div>
                    
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === getTotalPages()}
                    >
                      Next
                    </button>
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(getTotalPages())}
                      disabled={currentPage === getTotalPages()}
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}

              {isAssistantReallyLoading && (
                <div className="loading-overlay">
                  <div className="loading-spinner"></div>
                </div>
              )}
            </div>
          </div>
        )}
        {showModal && (
          // ... existing code ...
          <div className="modal-overlay">
            <div className="modal-content">
              <h3 style={{ color: "var(--danger, #d32f2f)", marginBottom: 8 }}>
                File already exists
              </h3>
              <p style={{ color: "var(--text, #333)", marginBottom: 24 }}>
                A file with this name already exists. Do you want to replace it?
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                <button
                  className="btn btn-danger"
                  style={{
                    background: "var(--danger, #d32f2f)",
                    color: "#fff",
                    borderRadius: 4,
                    padding: "8px 16px",
                    fontWeight: 600,
                    border: "none",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    display: "flex",
                    alignItems: "center",
                  }}
                  onClick={() =>
                    handleReuploadConfirmed(
                      pendingFile,
                      pendingFile.name || pendingFile.filename
                    )
                  }
                  disabled={isReuploading}
                >
                  {isReuploading ? (
                    <span className="loading-spinner small" style={{ marginRight: 8 }} />
                  ) : null}
                  Yes, replace
                </button>
                <button
                  className="btn btn-secondary"
                  style={{
                    background: "var(--secondary, #f5f5f5)",
                    color: "var(--text, #333)",
                    borderRadius: 4,
                    padding: "8px 16px",
                    fontWeight: 600,
                    border: "1px solid #ccc",
                  }}
                  onClick={handleCancelReupload}
                  disabled={isReuploading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
// ... existing code ...
        )}

        {selectedUser && (
          <div className="admin-section">
            <h2>User Details</h2>
            <div className="user-details">
              <div className="detail-row">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{selectedUser.email}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Role:</span>
                <span className="detail-value">{selectedUser.role}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Status:</span>
                <span className="detail-value">
                  {selectedUser.is_active ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Admin:</span>
                <span className="detail-value">
                  {selectedUser.is_admin ? "Yes" : "No"}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Created:</span>
                <span className="detail-value">
                  {new Date(selectedUser.creation_date).toLocaleString()}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Last Login:</span>
                <span className="detail-value">
                  {selectedUser.last_login
                    ? new Date(selectedUser.last_login).toLocaleString()
                    : "Never"}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Admin;
